import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useGetQuotesV2 } from "@rentablo/api/apiv1";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { FundRecommendationResponse } from "@rentablo/api/recommender";
import AiIcon from "@icons/myicons/ai.svg?react";
import { RecommendedItem } from "@features/investments/investment/hooks/useRecommededHistoricQuotes";
import { TooltipModal } from "../../components/Modal/TooltipModal";
import { useRecommenderImages } from "./hooks/useRecommenderImages";
import { RecommenderModalRow } from "./RecommenderModalRow";
import { setModalHiddenState } from "./storage";

interface RecommenderModalProps {
  isin: string;
  investmentId?: number;
  isOpen: boolean;
  onClose: () => void;
  recommendations: FundRecommendationResponse[];
  onSelect: (item: RecommendedItem, index: number) => void;
  selectedTickers: string[];
}

export const RecommenderModal = ({
  isin,
  investmentId,
  isOpen,
  onClose,
  onSelect,
  recommendations,
  selectedTickers,
}: RecommenderModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDownSM = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { images } = useRecommenderImages(recommendations);
  const { data: quotes } = useGetQuotesV2({
    securityId: recommendations.map((rec) => rec.fund.isin),
    quoteProvider: "ariva",
  });

  const recommendData = useMemo(() => {
    //TODO: Once the QuotesV2 endpoint is adapted to return the correct order or also return ISIN, the mapping will be removed/improved
    return recommendations?.map((rec) => ({
      ...rec,
      ticker: quotes?.quotes?.find((quote) =>
        quote.tickerSymbol.includes(rec.fund.isin)
      )?.tickerSymbol,
    }));
  }, [recommendations, quotes]);

  const onViewAllClick = () => {
    navigate(`/recommender/${isin}?investmentId=${investmentId}`);
  };

  const handleClose = () => {
    setModalHiddenState(investmentId?.toString() ?? "", true);
    onClose();
  };

  if (recommendData.length === 0) return <></>;

  return (
    <TooltipModal
      isOpen={isOpen}
      onClose={handleClose}
      onCloseButtonClick={handleClose}
      width="950px"
      maxHeight={isDownSM ? "100%" : "calc(100% - 30px)"}
      title={t("investment.recommendation.title")}
      height="fit-content"
      contentPadding={isDownSM ? theme.spacing(4) : theme.spacing(5, 6)}
      testId="recommendation-modal"
      titleVariant="body1"
      titleMargin={1}
      anchorPosition={{ top: isDownSM ? "0" : "-32px" }}
      outerSx={{
        paddingLeft: isDownSM ? "0" : "24px",
        paddingRight: isDownSM ? "0" : "24px",
      }}
    >
      <Typography
        variant={isDownSM ? "caption" : "body3"}
        color="textSecondary"
        mb={isDownSM ? 2 : 3}
      >
        {t("investment.recommendation.description")}
      </Typography>

      <Box mb={3} sx={{ overflowY: "auto" }}>
        {recommendData.map((rec, index) => (
          <RecommenderModalRow
            key={rec.fund.isin}
            recommendation={rec}
            images={images}
            isSelected={selectedTickers.includes(rec.ticker!)}
            index={index}
            onSelect={() => {
              onSelect(
                {
                  tickerSymbol: rec.ticker!,
                  name: rec.fund.name,
                  color: theme.palette.graphVisual[`0${6 + index}`]?.mid,
                },
                index
              );
            }}
          />
        ))}
      </Box>

      <Button
        variant="contained"
        color="gradient"
        fullWidth
        endIcon={<AiIcon height={20} width={20} />}
        onClick={onViewAllClick}
        sx={{
          padding: theme.spacing(1.25, 2),
          fontSize: "0.9rem",
          fontWeight: 600,
          textTransform: "none",
          borderRadius: "8px",
          "& .MuiButton-endIcon": { marginLeft: theme.spacing(1) },
        }}
      >
        {t("investment.recommendation.showAll")}
      </Button>
    </TooltipModal>
  );
};
